import * as i2 from '@angular/common/http';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject } from '@angular/core';
import * as i4 from '@upr-web-primer/app-utils';
import { param, paramBool } from '@upr-web-primer/app-utils';
import * as i5 from '@upr-web-primer/ms-guests';
import * as i1 from '@upr-web-primer/storage';
import { WINDOW } from '@upr-web-primer/window';
import * as i3 from 'ngx-cookie-service';
import { BehaviorSubject, EMPTY } from 'rxjs';
const SAP_CART_REPLACED_DEFAULTS = {
  sap_cart: {
    commerce_client_id: param('$sap_cart.commerce_client_id$'),
    commerce_client_secret_id: param('$sap_cart.commerce_client_secret_id$'),
    commerce_client_creds: param('$sap_cart.commerce_client_creds$'),
    is_cart_enabled: paramBool('$is_cart_enabled$'),
    occ_base_url: param('$carts.occ_base_url$'),
    base_site: param('$carts.base_site$')
  }
};
const SAP_CART_DEFAULTS = {
  sap_cart: {
    commerce_client_id: 'mobile_android',
    commerce_client_secret_id: 'secret',
    commerce_client_creds: 'client_credentials',
    is_cart_enabled: true,
    occ_base_url: '',
    base_site: ''
  }
};
const relativeUrls = {
  ANONYMOUS_CARTS: '/users/anonymous/carts/{guId}',
  CART_URL: 'occ/v2/',
  LOGGED_IN_CARTS: '/users/current/carts/current',
  LOGGED_IN_CART_ITEM: '/users/current/carts',
  CUSTOMER_TOKEN: 'authorizationserver/oauth/token',
  CUSTOMER_LOGIN: '/customers/login'
};
const SapCartCacheKeys = {
  SAP_CART: 'sapCart',
  SAP_AUTH_TOKEN: 'SAP_AUTH_TOKEN',
  AUTH_OBJ: 'AUTH_OBJ',
  LOGIN_AUTH_TOKEN: 'LOGIN_AUTH_TOKEN',
  CART_ITEM_COUNT: 'CART_ITEM_COUNT',
  O_AUTH_TOKEN: 'oAuthToken',
  SAP_CART_DEFAULT: 'sap_cart',
  ACCESS_TOKEN: 'access_token'
};
class SapCartService {
  constructor(localStorage, http, cookieService, window, appEnvSrv, msGuests) {
    this.localStorage = localStorage;
    this.http = http;
    this.cookieService = cookieService;
    this.window = window;
    this.appEnvSrv = appEnvSrv;
    this.msGuests = msGuests;
    this.quantity = 0;
    this.initialCartResponse = {
      cartLoaded: false,
      cart: {}
    };
    this.cartData = new BehaviorSubject(this.initialCartResponse);
    this.appEnvSrv.addDefaultValues(SAP_CART_DEFAULTS, SAP_CART_REPLACED_DEFAULTS);
  }
  /**
   * loadCart - Function to get cart item count for logged in and non-logged in users
   * @returns - Return cart details of logged in user
   */
  loadCart(env_name) {
    let cartDetails;
    this.sapCartDefaults = this.appEnvSrv.getEnvVariables(SapCartCacheKeys.SAP_CART_DEFAULT);
    const is_sign_in = this.msGuests.helper.isLoggedIn();
    if (this.sapCartDefaults.is_cart_enabled) {
      if (!is_sign_in) {
        // Set Cart Count for non-authorized user
        const sapCookie = this.cookieService.get(SapCartCacheKeys.SAP_CART);
        this.sapCart = sapCookie ? JSON.parse(this.cookieService.get(SapCartCacheKeys.SAP_CART)) : null;
        this.sapCartGuId = this.sapCart?.sapCartGuId;
        this.env_details = this.sapCart?.env_name;
        if (this.env_details && this.env_details === env_name) {
          this.getCartDetails(this.sapCartGuId).subscribe(cartResult => {
            if (cartResult) {
              if (cartResult?.entries?.length >= 0) {
                this.quantity = 0;
                cartResult?.entries.forEach(entry => {
                  this.quantity += entry?.quantity;
                });
              }
              this.updateGlobalNavCart(JSON.stringify(this.quantity));
              cartDetails = {
                cartLoaded: true,
                cart: cartResult
              };
              this.cartData.next(cartDetails);
            }
          });
        }
      } else {
        // Set Cart Count for authorized user
        const access_token = this.cookieService?.get(SapCartCacheKeys.ACCESS_TOKEN);
        if (access_token) {
          this.localStorage.setItem(SapCartCacheKeys.SAP_AUTH_TOKEN, access_token);
        }
        this.getB2COAuthToken().subscribe(authData => {
          this.localStorage.setItem(SapCartCacheKeys.AUTH_OBJ, JSON.stringify(authData));
          if (authData) {
            this.postauthLogin().subscribe(loginAPIResponse => {
              this.localStorage.setItem(SapCartCacheKeys.LOGIN_AUTH_TOKEN, JSON.stringify(loginAPIResponse));
              this.getActiveCarts();
            });
          }
        });
      }
    }
    if (this.cartData) {
      return this.cartData.asObservable();
    } else {
      return EMPTY;
    }
  }
  /**
   * getCartDetails - function to get cart details of current guId
   * @param guId - it contains guid of non-authorized user to get cart details
   * @returns - It will return given guid details contains entries
   */
  getCartDetails(guId) {
    let cartUrl = relativeUrls.ANONYMOUS_CARTS?.replace('{guId}', guId);
    const url = this.sapCartDefaults?.occ_base_url + relativeUrls.CART_URL + this.sapCartDefaults?.base_site + cartUrl;
    return this.http.get(url, {});
  }
  /**
   * postauthLogin - Commerce authentication call to get loginAuthtoken
   * @returns - It returns login details contains access token
   */
  postauthLogin() {
    const loggedInUserToken = JSON.parse(this.localStorage.getItem(SapCartCacheKeys.AUTH_OBJ));
    const jwtToken = this.localStorage.getItem(SapCartCacheKeys.O_AUTH_TOKEN) ?? this.localStorage.getItem(SapCartCacheKeys.SAP_AUTH_TOKEN);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${loggedInUserToken?.access_token}`,
      'guest-jwt': jwtToken
    });
    const params = new HttpParams().set('clientId', this.sapCartDefaults.commerce_client_id);
    return this.http.post(this.sapCartDefaults?.occ_base_url + relativeUrls.CART_URL + this.sapCartDefaults?.base_site + relativeUrls.CUSTOMER_LOGIN, {}, {
      headers,
      params
    });
  }
  /**
   * getB2COAuthToken - Token call to get auth object details
   * @returns - return response on commerce authentication
   */
  getB2COAuthToken() {
    const url = this.sapCartDefaults.occ_base_url + relativeUrls.CUSTOMER_TOKEN;
    const params = new HttpParams().set('client_id', encodeURIComponent(this.sapCartDefaults.commerce_client_id)).set('client_secret', encodeURIComponent(this.sapCartDefaults.commerce_client_secret_id)).set('grant_type', this.sapCartDefaults.commerce_client_creds);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    return this.http.post(url, params, {
      headers
    });
  }
  /**
   * getActiveCarts - Method to carts and current API to get cart details for logged in user
   */
  getActiveCarts() {
    let cartDetails;
    this.quantity = 0;
    this.fetchActiveCartItems().subscribe(items => {
      this.cartItemLength = items?.carts?.length;
      if (this.cartItemLength > 0) {
        this.fetchActiveCart().subscribe(cartItems => {
          if (cartItems?.entries?.length > 0) {
            this.quantity = 0;
            cartItems.entries.forEach(entry => {
              this.quantity += entry?.quantity;
            });
            this.updateGlobalNavCart(JSON.stringify(this.quantity));
          } else {
            this.localStorage.setItem(SapCartCacheKeys.CART_ITEM_COUNT, '0');
          }
          cartDetails = {
            cartLoaded: true,
            cart: items
          };
          this.cartData.next(cartDetails);
        });
      }
    });
  }
  /**
   * updateGlobalNavCart - Method to update cart item count in localStorage
   * @param newCartCount - cart item count
   */
  updateGlobalNavCart(newCartCount) {
    newCartCount = newCartCount ? newCartCount : '0';
    this.localStorage.setItem(SapCartCacheKeys.CART_ITEM_COUNT, newCartCount);
    this.refreshGlobalNav(newCartCount);
  }
  /**
   * refreshGlobalNav - Method to update cart item count in global nav
   * @param newCartCount - cart item count
   */
  refreshGlobalNav(newCartCount) {
    const oldCartCount = this.localStorage.getItem(SapCartCacheKeys.CART_ITEM_COUNT);
    const event = new CustomEvent('storage', {
      detail: {
        key: SapCartCacheKeys.CART_ITEM_COUNT,
        newCartCount,
        oldCartCount
      }
    });
    this.window.dispatchEvent(event);
  }
  /**
   * fetchActiveCart - Carts API call
   * @returns - return Cart Items
   */
  fetchActiveCart() {
    const loggedInUserToken = JSON.parse(this.localStorage.getItem(SapCartCacheKeys.LOGIN_AUTH_TOKEN));
    let httpHeaders = new HttpHeaders({});
    httpHeaders = httpHeaders.append('Authorization', `Bearer ${loggedInUserToken?.accessToken?.access_token}`);
    const options = {
      headers: httpHeaders
    };
    const relativeUrl = this.sapCartDefaults?.occ_base_url + relativeUrls.CART_URL + this.sapCartDefaults?.base_site + relativeUrls.LOGGED_IN_CARTS;
    return this.http.get(relativeUrl, options);
  }
  /**
   * fetchActiveCartItems - Current API call
   * @returns - return current cart Items
   */
  fetchActiveCartItems() {
    const loggedInUserToken = JSON.parse(this.localStorage.getItem(SapCartCacheKeys.LOGIN_AUTH_TOKEN));
    let httpHeaders = new HttpHeaders({});
    httpHeaders = httpHeaders.append('Authorization', `Bearer ${loggedInUserToken?.accessToken?.access_token}`);
    const options = {
      headers: httpHeaders
    };
    const url = this.sapCartDefaults?.occ_base_url + relativeUrls.CART_URL + this.sapCartDefaults?.base_site + relativeUrls.LOGGED_IN_CART_ITEM;
    return this.http.get(url, options);
  }
  static {
    this.ɵfac = function SapCartService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || SapCartService)(i0.ɵɵinject(i1.LocalStorageService), i0.ɵɵinject(i2.HttpClient), i0.ɵɵinject(i3.CookieService), i0.ɵɵinject(WINDOW), i0.ɵɵinject(i4.AppEnvironmentService), i0.ɵɵinject(i5.MsGuestsService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: SapCartService,
      factory: SapCartService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SapCartService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.LocalStorageService
  }, {
    type: i2.HttpClient
  }, {
    type: i3.CookieService
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }, {
    type: i4.AppEnvironmentService
  }, {
    type: i5.MsGuestsService
  }], null);
})();

/*
 * Public API Surface of sap-cart
 */

/**
 * Generated bundle index. Do not edit.
 */

export { SapCartService };
